import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { Link as RouterLink, useSearchParams } from "react-router";
import { EllipsisText } from "../../../modules/generic/components/ellipsis-text";
import { reviewStatusToPill, statusToPill } from "../../../modules/moment-card/utils";
import { LiveSearchField } from "../../../modules/search/live-search-field";
import { formatDate, fromISO8601 } from "../../../utils/iso8601";
import { TableStack } from "../../moments/analytics/send-table/table-stack";
import { statusFilterOptions, useReviews } from "../hooks/use-reviews";

import type { Review, ReviewsVariant } from "../hooks/use-reviews";
import type { SelectChangeEvent } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-pro";
import type { FC } from "react";

const emptyState: Record<ReviewsVariant, { title: string; subtitle: string }> = {
  "in-progress": {
    title: "There’s nothing to approve right now",
    subtitle:
      "You’ll receive an email notification when someone asks you to approve a Communication",
  },
  previous: {
    title: "Completed approvals will appear here",
    subtitle:
      "Once a Communication you have been asked to approve is published, it will appear here.",
  },
};

const ReviewsEmptyState: FC<{ variant: ReviewsVariant }> = ({ variant }) => (
  <Paper>
    <Stack spacing={1} sx={{ alignItems: "center", textAlign: "center", px: 3, py: 6 }}>
      <Typography variant="body1" sx={{ fontWeight: 700 }}>
        {emptyState[variant].title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {emptyState[variant].subtitle}
      </Typography>
    </Stack>
  </Paper>
);

const columns = (variant: ReviewsVariant): GridColDef<Review>[] => [
  {
    field: "title",
    headerName: "Communication",
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => (
      <TableStack>
        <Link
          component={RouterLink}
          to={`/moments/${row.moment_id}/review`}
          data-analytics-id="reviews-communication-link"
          underline="hover"
        >
          {row.title}
        </Link>
      </TableStack>
    ),
  },
  { field: "creator", headerName: "Creator", width: 180 },
  {
    field: "status",
    headerName: "Status",
    width: 180,
    valueGetter: (_value, row) => (variant === "previous" ? row.momentStatus : row.reviewStatus),
    renderCell: ({ row }) => (
      <TableStack>
        {variant === "previous"
          ? statusToPill(row.momentStatus)
          : reviewStatusToPill(row.reviewStatus, "soft")}
      </TableStack>
    ),
  },
  {
    field: "requestedAt",
    headerName: "Requested",
    width: 200,
    valueFormatter: (value: Review["requestedAt"]) => formatDate(fromISO8601(value)),
  },
  {
    field: "message",
    headerName: "Message",
    flex: 1,
    maxWidth: 360,
    renderCell: ({ row }) =>
      row.message && (
        <TableStack>
          <EllipsisText text={row.message} lines={2} variant="body2" />
        </TableStack>
      ),
  },
];

export const ReviewsTable: FC<{ variant: ReviewsVariant }> = ({ variant }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "";
  const { reviews, loading, error } = useReviews(variant);

  const handleStatusChange = (event: SelectChangeEvent): void => {
    const params = new URLSearchParams(searchParams);
    if (event.target.value) params.set("status", event.target.value);
    else params.delete("status");
    setSearchParams(params);
  };

  if (!loading && !error && reviews.length === 0) return <ReviewsEmptyState variant={variant} />;

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="reviews-status-filter-label">Status</InputLabel>
          <Select
            labelId="reviews-status-filter-label"
            label="Status"
            value={status}
            onChange={handleStatusChange}
          >
            <MenuItem value="">All</MenuItem>
            {statusFilterOptions[variant].map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ minWidth: 280, maxWidth: 400 }}>
          <LiveSearchField label="Search approvals" size="small" />
        </Box>
      </Stack>

      <Paper>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">Unable to load approvals</Alert>}
        <DataGridPro<Review>
          rows={reviews}
          columns={columns(variant)}
          getRowId={(row) => row.moment_id}
          getRowHeight={() => "auto"}
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooter
          autoHeight
          sx={{ border: 0 }}
        />
      </Paper>
    </>
  );
};
